import type { Component } from 'vue'

import type { ProductCategory } from '@backmarket/http-api/src/api-specs-content/models/product-category'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconCpu } from '@ds/icons/IconCpu'
import { IconSmartphoneBroken } from '@ds/icons/IconSmartphoneBroken'
import { IconSmartphoneScreenSize } from '@ds/icons/IconSmartphoneScreenSize'

import translations from './ProductGrades.translations'

export const GRADES = ['fair', 'good', 'excellent', 'premium'] as const

export type Grade = (typeof GRADES)[number]
type ProductDetail = {
  icon: Component
  title: I18nDefinition
  text: Record<ProductCategory, Record<string, I18nDefinition>>
}

export function getProductDetails(category: ProductCategory): ProductDetail[] {
  const details: ProductDetail[] = [
    {
      icon: IconSmartphoneScreenSize,
      title: translations.screenTitle,
      text: {
        smartphones: {
          fair: translations.screenTextFair,
          good: translations.screenTextGood,
          excellent: translations.screenTextExcellent,
          premium: translations.screenTextPremium,
        },
        tablets: {
          fair: translations.screenTextFair,
          good: translations.screenTextGood,
          excellent: translations.screenTextExcellent,
          premium: translations.screenTextPremium,
        },
        laptops: {
          fair: translations.screenTextFair,
          good: translations.screenTextGood,
          excellent: translations.screenTextExcellent,
          premium: translations.screenTextPremium,
        },
        consoles: {
          fair: translations.screenTextFair,
          good: translations.screenTextGood,
          excellent: translations.screenTextExcellent,
          premium: translations.screenTextPremium,
        },
        smartwatches: {
          fair: translations.screenTextFair,
          good: translations.screenTextGood,
          excellent: translations.screenTextExcellent,
          premium: translations.screenTextPremium,
        },
      },
    },
    {
      icon: IconSmartphoneBroken,
      title: translations.bodyTitle,
      text: {
        smartphones: {
          fair: translations.bodyTextFair,
          good: translations.bodyTextGood,
          excellent: translations.bodyTextExcellent,
          premium: translations.bodyTextPremium,
        },
        tablets: {
          fair: translations.bodyTextFair,
          good: translations.bodyTextGood,
          excellent: translations.bodyTextExcellent,
          premium: translations.bodyTextPremium,
        },
        laptops: {
          fair: translations.bodyTextFair,
          good: translations.bodyTextGood,
          excellent: translations.bodyTextExcellent,
          premium: translations.bodyTextPremium,
        },
        consoles: {
          fair: translations.bodyTextFair,
          good: translations.bodyTextGood,
          excellent: translations.bodyTextExcellent,
          premium: translations.bodyTextPremium,
        },
        smartwatches: {
          fair: translations.bodyTextFair,
          good: translations.bodyTextGood,
          excellent: translations.bodyTextExcellent,
          premium: translations.bodyTextPremium,
        },
      },
    },
    {
      icon: IconCpu,
      title: translations.hardwareTitle,
      text: {
        smartphones: {
          fair: translations.hardwareTextFair,
          good: translations.hardwareTextGood,
          excellent: translations.hardwareTextExcellent,
          premium: translations.hardwareTextPremium,
        },
        tablets: {
          fair: translations.hardwareTextFair,
          good: translations.hardwareTextGood,
          excellent: translations.hardwareTextExcellent,
          premium: translations.hardwareTextPremium,
        },
        laptops: {
          fair: translations.hardwareTextFair,
          good: translations.hardwareTextGood,
          excellent: translations.hardwareTextExcellent,
          premium: translations.hardwareTextPremium,
        },
        consoles: {
          fair: translations.hardwareTextFair,
          good: translations.hardwareTextGood,
          excellent: translations.hardwareTextExcellent,
          premium: translations.hardwareTextPremium,
        },
        smartwatches: {
          fair: translations.hardwareTextFair,
          good: translations.hardwareTextGood,
          excellent: translations.hardwareTextExcellent,
          premium: translations.hardwareTextPremium,
        },
      },
    },
  ]

  if (category !== 'consoles') {
    details.push({
      icon: IconBattery,
      title: translations.batteryTitle,
      text: {
        smartphones: {
          fair: translations.batteryTextSmartphonesFair,
          good: translations.batteryTextSmartphonesGood,
          excellent: translations.batteryTextSmartphonesExcellent,
          premium: translations.batteryTextSmartphonesPremium,
        },
        tablets: {
          fair: translations.batteryTextTabletsFair,
          good: translations.batteryTextTabletsGood,
          excellent: translations.batteryTextTabletsExcellent,
          premium: translations.batteryTextTabletsPremium,
        },
        laptops: {
          fair: translations.batteryTextLaptopsFair,
          good: translations.batteryTextLaptopsGood,
          excellent: translations.batteryTextLaptopsExcellent,
          premium: translations.batteryTextLaptopsPremium,
        },
        consoles: {
          fair: translations.batteryTextGamingConsolesFair,
          good: translations.batteryTextGamingConsolesGood,
          excellent: translations.batteryTextGamingConsolesExcellent,
          premium: translations.batteryTextGamingConsolesPremium,
        },
        smartwatches: {
          fair: translations.batteryTextSmartwatchesFair,
          good: translations.batteryTextSmartwatchesGood,
          excellent: translations.batteryTextSmartwatchesExcellent,
          premium: translations.batteryTextSmartwatchesPremium,
        },
      },
    })
  }

  return details
}

export const categoriesData: Record<
  ProductCategory,
  {
    packshotsPath: string
    startProgress: number
    hasPremiumGrade: boolean
    index: number
    image: { src: string; alt: string; width: number; height: number }
  }
> = {
  smartphones: {
    packshotsPath:
      'https://creative-pages.statics.backmarket.com/verified-refurbished/packshots/smartphones/webp/',
    startProgress: 0.41,
    hasPremiumGrade: true,
    index: 2,
    image: {
      src: '/img/verified-refurbished/hero/iphone.png',
      alt: 'Iphone',
      width: 336,
      height: 403,
    },
  },
  tablets: {
    packshotsPath:
      'https://creative-pages.statics.backmarket.com/verified-refurbished/packshots/tablets/webp/',
    startProgress: 0.42,
    hasPremiumGrade: false,
    index: 0,
    image: {
      src: '/img/verified-refurbished/hero/tablet.png',
      alt: 'Tablet',
      width: 336,
      height: 403,
    },
  },
  consoles: {
    packshotsPath:
      'https://creative-pages.statics.backmarket.com/verified-refurbished/packshots/consoles/webp/',
    startProgress: 0.85,
    hasPremiumGrade: false,
    index: 1,
    image: {
      src: '/img/verified-refurbished/hero/console.png',
      alt: 'Console',
      width: 336,
      height: 403,
    },
  },
  laptops: {
    packshotsPath:
      'https://creative-pages.statics.backmarket.com/verified-refurbished/packshots/laptops/webp/',
    startProgress: 0.35,
    hasPremiumGrade: false,
    index: 3,
    image: {
      src: '/img/verified-refurbished/hero/laptop.png',
      alt: 'Laptop',
      width: 336,
      height: 403,
    },
  },
  smartwatches: {
    packshotsPath:
      'https://creative-pages.statics.backmarket.com/verified-refurbished/packshots/smartwatches/webp/',
    startProgress: 0.83,
    hasPremiumGrade: false,
    index: 4,
    image: {
      src: '/img/verified-refurbished/hero/smartwatch.png',
      alt: 'Smartwatch',
      width: 336,
      height: 403,
    },
  },
}

type Picker = {
  label: I18nDefinition
  grade: Grade
}

const { fairLabel, goodLabel, excellentLabel, premiumLabel } = translations
export function buildPickers(category?: Maybe<ProductCategory>) {
  const pickers: Picker[] = [
    {
      label: fairLabel,
      grade: 'fair',
    },
    {
      label: goodLabel,
      grade: 'good',
    },
    {
      label: excellentLabel,
      grade: 'excellent',
    },
  ]

  if (category === 'smartphones') {
    pickers.push({
      label: premiumLabel,
      grade: 'premium',
    })
  }

  return pickers
}
